import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, MapPin, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TypingPlaceholder } from '@/components/ui/typing-placeholder';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunity } from '@/contexts/CommunityContext';
import { useToast } from '@/hooks/use-toast';
import { useImageUpload } from '@/hooks/useImageUpload';
import { CreateServiceRequestData, ServiceRequestUrgency } from '@/types/serviceRequest';
import { ServiceRequestService } from '@/services/serviceRequestService';
import { encodeHashData, cleanFormData, getCurrentUrlHash, decodeHashData, debounce, updateUrlHash } from '@/lib/hashUtils';
import { FadeIn } from '@/components/Animations';
import Layout from '@/components/Layout';
import { supabase } from '@/integrations/supabase/client';
import { PATH } from '@/routes/routeConfig';

const ServiceRequest: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceRequestId } = useParams<{ serviceRequestId?: string }>();
  const { user } = useAuth();
  const { currentCommunity } = useCommunity();
  const { toast } = useToast();
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const [formData, setFormData] = useState({
    communityId: currentCommunity?.id || '',
    originalRequest: '',
    urgency: 'in_the_week' as ServiceRequestUrgency
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hashDataLoaded, setHashDataLoaded] = useState(false);

  // Debounced function to save form data to URL hash
  const debouncedSaveToHash = useRef(
    debounce((data: typeof formData) => {
      if (data.originalRequest.trim()) {
        const hashData = {
          searchParams: cleanFormData(data),
          timestamp: Date.now()
        };
        const hash = encodeHashData(hashData);
        updateUrlHash(hash);
      }
    }, 1000)
  ).current;
  
  // Load existing service request data if serviceRequestId is provided
  useEffect(() => {
    if (serviceRequestId) {
      const loadServiceRequest = async () => {
        setIsLoading(true);
        try {
          const serviceRequest = await ServiceRequestService.getServiceRequest(serviceRequestId);
          if (serviceRequest) {
            setFormData(prev => ({
              ...prev,
              originalRequest: serviceRequest.original_request,
              urgency: serviceRequest.urgency || 'in_the_week'
            }));
          }
        } catch (error) {
          console.error('Error loading service request:', error);
          toast({
            title: "Error",
            description: t('service_request.error.loading_failed'),
            variant: "destructive"
          });
        } finally {
          setIsLoading(false);
        }
      };
      
      loadServiceRequest();
    }
  }, [serviceRequestId, toast]);

  // Load form data from URL hash on page load
  useEffect(() => {
    if (!serviceRequestId) { // Only load from hash if not editing existing request
      const hash = getCurrentUrlHash();
      if (hash) {
        const hashData = decodeHashData<{ searchParams?: typeof formData; matches?: unknown }>(hash);
        if (hashData?.searchParams) {
          setFormData(prev => ({
            ...prev,
            ...hashData.searchParams
          }));
          setHashDataLoaded(true);
        }
      }
    }
  }, [serviceRequestId]);

  useEffect(() => {
    if (currentCommunity?.id) {
      setFormData(prev => ({ ...prev, communityId: currentCommunity.id }));
    }
  }, [currentCommunity]);

  // Save form data to hash with debouncing
  useEffect(() => {
    // Only update hash if we're actually on the ServiceRequest route
    const isOnServiceRequestRoute = location.pathname === PATH.SERVICE_REQUEST || location.pathname.startsWith('/service-request');
    
    if (!serviceRequestId && currentCommunity?.id && isOnServiceRequestRoute) {
      debouncedSaveToHash(formData);
    }
  }, [formData, serviceRequestId, currentCommunity?.id, location.pathname]);
  
  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  }, [formData.originalRequest]);
  
  // Image upload functionality
  const {
    pendingImages,
    addImages,
    removeImage,
    hasImages
  } = useImageUpload();


  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      addImages(files);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !currentCommunity) {
      toast({
        title: "Error",
        description: t('service_request.error.login_required'),
        variant: "destructive"
      });
      return;
    }

    if (!formData.originalRequest.trim()) {
      toast({
        title: "Error", 
        description: t('service_request.error.description_required'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      console.log('Searching for business matches...');
      
      // Call search-business-matches edge function using Supabase client
      const { data: result, error } = await supabase.functions.invoke('search-business-matches', {
        body: {
          searchParams: {
            communityId: formData.communityId,
            originalRequest: formData.originalRequest.trim(),
            urgency: formData.urgency
          }
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to search business matches');
      }

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to search business matches');
      }

      console.log('Found business IDs:', result.count);
      
      // Combine search params with business IDs for URL hash
      const combinedData = {
        searchParams: formData,
        businessIds: result.businessIds || [],
        count: result.count || 0,
        cached: result.cached || false,
        timestamp: Date.now()
      };

      // Encode data to URL hash
      const hash = encodeHashData(combinedData);
      
      // Navigate to review page with results in URL hash
      navigate(`${PATH.REVIEW_POTENTIAL_MATCH}#${hash}`);
      
    } catch (error) {
      console.error('Error searching business matches:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : 'Failed to search for businesses. Please try again.',
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading state while loading existing service request
  if (isLoading) {
    return (
      <Layout>
        <div className="container mx-auto py-6 md:py-8 px-4 bg-white">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">{t('service_request.loading.service_request')}</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.4 }}
        className="container mx-auto py-8 md:py-10 px-4 bg-white"
      >
        <div className="max-w-2xl mx-auto">
          {/* Page Header */}
          <FadeIn direction="up" delay={0.1}>
            <div className="mb-6 md:mb-8 lg:mb-10">
              <h1 className="text-2xl lg:text-3xl font-bold mb-3 md:mb-4">
                {serviceRequestId ? t('service_request.page.title.refine') : t('service_request.page.title.create')}
              </h1>
              <p className="text-sm lg:text-base text-muted-foreground">
                {serviceRequestId 
                  ? t('service_request.page.subtitle.refine')
                  : t('service_request.page.subtitle.create')
                }
              </p>
            </div>
          </FadeIn>

          {/* Form */}
          <FadeIn direction="up" delay={0.2}>
            <form onSubmit={handleSubmit} className="space-y-6 md:space-y-7 lg:space-y-8">
              
              {/* Service Request Form */}
              <Card className="shadow-lg border border-gray-200 bg-white">
                <CardContent className="p-6">
                  <div className="space-y-6">
                    {/* Service Description */}
                    <div className="space-y-3">
                      <label className="text-base font-semibold text-gray-800">
                        {t('service_request.form.question')}
                      </label>
                      <div className="relative">
                        <Textarea
                          ref={textareaRef}
                          placeholder=""
                          value={formData.originalRequest}
                          onChange={(e) => setFormData(prev => ({ 
                            ...prev, 
                            originalRequest: e.target.value 
                          }))}
                          className="w-full min-h-[160px] resize-none border border-gray-200 bg-gray-50 rounded-lg p-4 text-base leading-relaxed placeholder:text-gray-400 overflow-hidden transition-colors duration-200 focus:outline-0 focus:ring-0 focus:border-primary/60 focus:bg-white focus:shadow-none focus-visible:outline-0 focus-visible:ring-0 [-webkit-appearance:none] [-webkit-tap-highlight-color:transparent] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                          required
                        />
                        {!formData.originalRequest && (
                          <div className="absolute top-4 left-4 right-4 pointer-events-none">
                            <TypingPlaceholder
                              sentences={[
                                t('service_request.typing.example_1'),
                                t('service_request.typing.example_2')
                              ]}
                              typingSpeed={40}
                              pauseBetweenSentences={3000}
                              className="text-base leading-relaxed w-full"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-2">
                      <Button
                        type="submit"
                        disabled={isSubmitting || !formData.originalRequest.trim()}
                        className="w-full py-3 text-base font-semibold rounded-lg bg-primary hover:bg-primary/90 text-white transition-all flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Searching...</span>
                          </>
                        ) : (
                          <>
                            <span>{t('service_request.button.find_services')}</span>
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
          </form>
        </FadeIn>

        </div>
      </motion.div>
    </Layout>
  );
};

export default ServiceRequest;